import { useState, type FormEvent } from 'react'
import { cn } from '../utils'
import { Send, CheckCircle2 } from 'lucide-react'

export type InviteMemberFormProps = {
  onSubmit: (email: string) => Promise<void>
  className?: string
}

export function InviteMemberForm({ onSubmit, className }: InviteMemberFormProps) {
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed || submitting) return
    setSubmitting(true)
    setError(null)
    setSuccess(null)
    try {
      await onSubmit(trimmed)
      setSuccess(`Invitation sent to ${trimmed}`)
      setEmail('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={cn('space-y-2', className)}>
      <form onSubmit={handleSubmit} className="flex items-center gap-3">
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(null); setSuccess(null) }}
          placeholder="Invite by email address"
          className="flex-1 rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary"
          disabled={submitting}
        />
        <button
          type="submit"
          disabled={!email.trim() || submitting}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none transition-colors"
        >
          <Send className="h-4 w-4" />
          {submitting ? 'Inviting...' : 'Invite'}
        </button>
      </form>
      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}
      {success && (
        <p className="text-xs text-status-success-foreground flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" />
          {success}
        </p>
      )}
    </div>
  )
}
